import C from "@/modules/main/TopNav.module.css";

import { Spacer } from "@/components/Spacer.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { useConfig } from "@/config.ts";
import { Isolated, IsolatedChildren } from "@/modules/error/Isolated.tsx";
import { LocaleNavItem } from "@/modules/l10n/LocaleButton.tsx";
import { languagesEnabledAtom } from "@/modules/l10n/i18n.store.ts";
import { hasDebugBarAtom } from "@/modules/main/debug/DebugMenu.store.ts";
import { mcn, type BaseProps } from "@/utils/baseProps.ts";
import { cn } from "@/utils/classNames.ts";
import { Trans } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import React, { Suspense, memo } from "react";

// NB: Lazily load all of the child components to reduce the initial bundle size (of which `TopNav` is a part).
const ImpersonateTopItem = React.lazy(() => import("@/modules/user/ImpersonateTopItem.tsx"));
const PaymentTopItem = React.lazy(() => import("@/modules/billing/PaymentTopItem.tsx"));
const NotificationMenu = React.lazy(() => import("@/modules/notifications/NotificationMenu.tsx"));
const UserMenu = React.lazy(() => import("@/modules/user/UserMenu.tsx"));
const DebugButton = React.lazy(() => import("@/modules/main/debug/DebugButton.tsx"));
const DebugBar = React.lazy(() => import("@/modules/main/debug/DebugMenu.tsx"));
const ColorSchemeMenu = React.lazy(() => import("@/modules/main/ColorSchemeMenu.tsx"));
const Hamburger = React.lazy(() => import("@/modules/main/mobile/Hamburger.tsx"));
const CurrencyButton = React.lazy(() => import("@/modules/currency/CurrencyModal.tsx"));

/** Top navigation bar for the entire page */
export const TopNav = memo(function TopNav(props: BaseProps) {
    const hasDebugBar = useAtomValue(hasDebugBarAtom);
    const languageEnabled = useAtomValue(languagesEnabledAtom);

    const header = (
        <header {...mcn(cn("HStack", C.TopNav), props)}>
            <TopLogo />
            <Spacer />
            <IsolatedChildren>
                <PaymentTopItem />
                <CurrencyButton />
                <SupportLink />
                {languageEnabled && <LocaleNavItem />}
                <ColorSchemeMenu />
                <NotificationMenu />
                {hasDebugBar && <DebugButton />}
                <UserMenu />
            </IsolatedChildren>
        </header>
    );

    return (
        <>
            <Isolated>
                <ImpersonateTopItem />
            </Isolated>
            {header}
            {hasDebugBar && (
                <Suspense>
                    <DebugBar />
                </Suspense>
            )}
        </>
    );
});

export const MobileTopNav = memo(function MobileTopNav(props: BaseProps) {
    const languageEnabled = useAtomValue(languagesEnabledAtom);

    const header = (
        <header {...mcn(`HStack ${C.TopNav} ${C.Mobile}`, props)}>
            <Suspense>
                <Hamburger />
            </Suspense>
            <TopLogo />
            <Spacer />
            <IsolatedChildren>
                <CurrencyButton />
                <NotificationMenu />
                <ColorSchemeMenu />
                {languageEnabled && <LocaleNavItem />}
                <UserMenu />
            </IsolatedChildren>
        </header>
    );

    return (
        <div>
            <Isolated>
                <ImpersonateTopItem isMobile />
            </Isolated>
            {header}
        </div>
    );
});

function TopLogo() {
    return <Link to="/" aria-label="Home" className={`bg-icon ${C.logo}`} />;
}

function SupportLink() {
    const { supportUrl } = useConfig();
    return (
        !!supportUrl && (
            <WButton
                color="primary"
                variant="ghost"
                size="bar"
                ariaLabel="Contact support"
                action={{ href: supportUrl, target: "_blank" }}
                icon="jp-icon-question-mark"
            >
                <span className="hidden-mobile color-text">
                    <Trans comment="Button label. Short of: Contact customer support">Support</Trans>
                </span>
            </WButton>
        )
    );
}
